import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { FilterChip } from "@/components/ui/FilterChip";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import { ApiError } from "@/lib/api";
import {
  buildOperationalLocationResolveIndex,
  operationalLocationFilterCode,
  resolveOperationalLocationTokenOrFallback,
  type OperationalLocationResolved,
} from "@/lib/operationalLocationFilter";
import { operationalLocationSelectRank, useTransferEndpointLocations } from "@/lib/transferEndpointLocations";
import { downloadSkuInventoryTransactionContextCsv } from "@/pages/manufacturing/skuInventoryTransactionContextCsv";
import { SkuInventoryTransactionContextTable } from "@/pages/manufacturing/SkuInventoryTransactionContextTable";
import { type InventoryDiscrepancySkuContextRowWithRunningTotal } from "@/pages/manufacturing/inventoryDiscrepancySkuContext";
import {
  attachSkuContextBalanceColumns,
  type SkuContextBalanceAnchors,
} from "@/pages/manufacturing/skuContextBalances";

import { fetchSkuTransactionHistory, type SkuTransactionHistoryRow } from "./skuTransactionHistoryApi";

const SKU_TRANSACTION_HISTORY_STALE_MS = 30_000;

function transactionHistoryLocationKey(row: SkuTransactionHistoryRow): string {
  return row.location_code?.trim() ?? "";
}

function resolveRowLocation(
  row: SkuTransactionHistoryRow,
  index: ReadonlyMap<string, OperationalLocationResolved>,
): OperationalLocationResolved {
  return resolveOperationalLocationTokenOrFallback(transactionHistoryLocationKey(row), index);
}

export function SkuTransactionHistoryTab(props: { sku: string }): JSX.Element {
  const [locationFilter, setLocationFilter] = useState<Set<string>>(() => new Set());
  const { locations: transferLocations } = useTransferEndpointLocations();

  const locationResolveIndex = useMemo(
    () => buildOperationalLocationResolveIndex(transferLocations),
    [transferLocations],
  );

  const showRunningTotal = true;
  const showLocationColumn = true;

  const historyQuery = useQuery({
    queryKey: ["mrp", "skus", "transaction-history", "balances", props.sku],
    queryFn: () => fetchSkuTransactionHistory(props.sku),
    enabled: props.sku.length > 0,
    staleTime: SKU_TRANSACTION_HISTORY_STALE_MS,
    refetchOnWindowFocus: false,
  });

  const rows = historyQuery.data?.rows ?? [];

  const locationOptions = useMemo(() => {
    const byFilterCode = new Map<string, OperationalLocationResolved>();
    for (const row of rows) {
      const resolved = resolveRowLocation(row, locationResolveIndex);
      if (resolved.filterCode === "") {
        continue;
      }
      const existing = byFilterCode.get(resolved.filterCode);
      if (existing == null || existing.displayName.length < resolved.displayName.length) {
        byFilterCode.set(resolved.filterCode, resolved);
      }
    }
    const rankForFilterCode = (filterCode: string): number => {
      const location = transferLocations.find(
        (entry) => operationalLocationFilterCode(entry) === filterCode,
      );
      return location != null ? operationalLocationSelectRank(location) : 100;
    };
    return [...byFilterCode.values()].sort((left, right) => {
      const rank = rankForFilterCode(left.filterCode) - rankForFilterCode(right.filterCode);
      if (rank !== 0) {
        return rank;
      }
      return left.displayName.localeCompare(right.displayName);
    });
  }, [rows, locationResolveIndex, transferLocations]);

  const filteredRows = useMemo(() => {
    if (locationFilter.size === 0) {
      return rows;
    }
    return rows.filter((row) => locationFilter.has(resolveRowLocation(row, locationResolveIndex).filterCode));
  }, [rows, locationFilter, locationResolveIndex]);

  const onHandByLocationId = useMemo(() => {
    const balances = new Map<number, number>();
    for (const entry of historyQuery.data?.on_hand_by_location ?? []) {
      if (Number.isFinite(entry.location_id) && Number.isFinite(entry.on_hand)) {
        balances.set(entry.location_id, entry.on_hand);
      }
    }
    return balances;
  }, [historyQuery.data?.on_hand_by_location]);

  const balanceAnchors = useMemo((): SkuContextBalanceAnchors | null => {
    const entries = historyQuery.data?.balances_by_location;
    if (entries == null) {
      if (historyQuery.data?.on_hand_by_location == null) {
        return null;
      }
      let ledger = 0;
      if (locationFilter.size === 0) {
        for (const onHand of onHandByLocationId.values()) {
          ledger += onHand;
        }
      } else {
        for (const filterCode of locationFilter) {
          const location = transferLocations.find(
            (entry) => operationalLocationFilterCode(entry) === filterCode,
          );
          if (location != null) {
            ledger += onHandByLocationId.get(location.id) ?? 0;
          }
        }
      }
      return { ledger, warehouse: ledger, floor: 0, receiving: 0 };
    }
    const selectedLocationIds = new Set<number>();
    if (locationFilter.size > 0) {
      for (const filterCode of locationFilter) {
        const location = transferLocations.find(
          (entry) => operationalLocationFilterCode(entry) === filterCode,
        );
        if (location != null) {
          selectedLocationIds.add(location.id);
        }
      }
    }
    let ledger = 0;
    let receiving = 0;
    let floor = 0;
    let shippedOpen = 0;
    for (const entry of entries) {
      if (locationFilter.size > 0 && !selectedLocationIds.has(entry.location_id)) {
        continue;
      }
      ledger += entry.ledger;
      receiving += entry.receiving;
      floor += entry.floor;
      shippedOpen += entry.shipped_open;
    }
    return {
      ledger,
      receiving,
      floor,
      warehouse: ledger - floor - shippedOpen,
    };
  }, [historyQuery.data, locationFilter, onHandByLocationId, transferLocations]);

  const rowsWithTotals = useMemo((): InventoryDiscrepancySkuContextRowWithRunningTotal[] => {
    if (balanceAnchors == null) {
      return filteredRows.map((row) => ({
        ...row,
        on_hand_total: null,
        in_house_total: null,
        ledger_total: null,
        warehouse_total: null,
        floor_total: null,
        receiving_total: null,
      }));
    }
    return attachSkuContextBalanceColumns(filteredRows, balanceAnchors);
  }, [filteredRows, balanceAnchors]);

  const toggleLocation = (filterCode: string): void => {
    setLocationFilter((current) => {
      const next = new Set(current);
      if (next.has(filterCode)) {
        next.delete(filterCode);
      } else {
        next.add(filterCode);
      }
      return next;
    });
  };

  const locationLabelForRow = (
    row: InventoryDiscrepancySkuContextRowWithRunningTotal & { location_code?: string | null },
  ): JSX.Element => {
    const resolved = resolveRowLocation(
      { ...row, location_code: row.location_code ?? null },
      locationResolveIndex,
    );
    const location = transferLocations.find((entry) => entry.id === resolved.locationId);
    const abbreviation = location?.abbreviation?.trim() || resolved.displayName;
    if (resolved.locationId > 0) {
      return (
        <ManufacturingLocationChip
          locationId={resolved.locationId}
          fallbackLabel={abbreviation}
        />
      );
    }
    return <span>{abbreviation}</span>;
  };

  return (
    <div className="space-y-3">
      {locationOptions.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Locations</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-xs text-slate-500">
              No selection shows every location. Turn on one or more locations to filter the rows.
              Ledger is the Megaventory total. Warehouse is on the shelf. Floor is picked to work in
              process. Receiving is receipts that do not have a purchase invoice yet.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {locationOptions.map((option) => {
                const isOn = locationFilter.has(option.filterCode);
                return (
                  <FilterChip
                    key={option.filterCode}
                    pressed={isOn}
                    onClick={() => toggleLocation(option.filterCode)}
                  >
                    {option.displayName}
                  </FilterChip>
                );
              })}
            </div>
          </CardBody>
        </Card>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900">Transaction history</h3>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="gap-1"
          disabled={historyQuery.isLoading || rowsWithTotals.length === 0}
          onClick={() => downloadSkuInventoryTransactionContextCsv(props.sku, rowsWithTotals)}
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          Download CSV
        </Button>
      </div>

      <SkuInventoryTransactionContextTable
        layout="viewport"
        rows={rowsWithTotals}
        loading={historyQuery.isLoading}
        loadingMessage="Loading transaction history…"
        error={historyQuery.error ? (historyQuery.error as ApiError).message : null}
        emptyMessage={
          locationFilter.size > 0
            ? "No rows match the selected locations."
            : "No purchase orders, sales orders, transfers, work orders, or adjustments for this SKU yet."
        }
        showRunningTotal={false}
        showBalanceColumns={showRunningTotal}
        showLocationColumn={showLocationColumn}
        locationLabelForRow={locationLabelForRow}
      />
    </div>
  );
}
